import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth, AuthProvider } from "./contexts/AuthContext";
import WebSocketMiddleware from "./middleware/WebSocketMiddleware";
import { useTheme, ThemeProvider } from "./contexts/ThemeContext";
import LoadingSpinner from "./components/ui/LoadingSpinner";
import ChatBot from "./components/common/ChatBot";
import BBTLaunchBanner from "./components/common/BBTLaunchBanner";
import LaunchPromoPopup from './components/common/LaunchPromoPopup';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0D1B2A', color: 'white', fontFamily: 'system-ui, sans-serif' }}>
          <div style={{ textAlign: 'center', padding: '2rem', maxWidth: '500px' }}>
            <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Baobab Exchange</h1>
            <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>
              Une erreur inattendue s'est produite. Veuillez rafraichir la page.
            </p>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); window.location.href = '/'; }}
              style={{ padding: '12px 32px', background: '#00A04A', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1rem', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Retour a l'accueil
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Layouts
import MainLayout from "./components/layout/MainLayout";
import AdminLayout from "./components/admin/AdminLayout";
import PublicLayout from "./components/layout/PublicLayout";
import AuthLayout from "./components/layout/AuthLayout";

// Pages publiques chargées immédiatement (plus critiques)
import Home from "./pages/Home";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import Error404 from "./pages/Error404";
import EmailVerificationSent from "./pages/auth/EmailVerificationSent";

// Lazy loading des pages moins critiques
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));
const ResendVerification = lazy(() => import("./pages/auth/ResendVerification"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const BBTPage = lazy(() => import("./pages/BBTPage"));
const AirdropPage = lazy(() => import("./pages/AirdropPage"));
const Tokenomics = lazy(() => import("./pages/Tokenomics"));
const Terms = lazy(() => import("./pages/legal/TermsOfService"));
const Privacy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const CookiesPolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const AmlKycPolicy = lazy(() => import("./pages/legal/AMLKYCPolicy"));
const About = lazy(() => import("./pages/About"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Support = lazy(() => import("./pages/Support"));
const Blog = lazy(() => import("./pages/Blog"));
const FeesPage = lazy(() => import("./pages/FeesPage"));

// Lazy loading des pages utilisateur
const Exchange = lazy(() => import("./pages/Exchange"));
const Transactions = lazy(() => import("./pages/Transactions"));
const TransactionDetail = lazy(() => import("./components/transactions/TransactionDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const Wallet = lazy(() => import("./pages/Wallet"));
const Settings = lazy(() => import("./pages/Settings"));
const TransferMoney = lazy(() => import("./pages/TransferMoney"));
const ConfirmTransaction = lazy(() => import("./pages/ConfirmTransaction"));
const TransactionProcessing = lazy(() => import("./pages/TransactionProcessing"));
const Affiliation = lazy(() => import("./pages/Affiliation"));
const MoneyOps = lazy(() => import("./pages/MoneyOps"));
const P2PTransferPage = lazy(() => import('./pages/P2PTransferPage'));
const RussianBankDeposit = lazy(() => import('./pages/RussianBankDeposit'));
const TelegramTransfer = lazy(() => import('./pages/TelegramTransfer'));

// DEX pages
const SwapPage = lazy(() => import('./pages/dex/SwapPage'));
const AddLiquidity = lazy(() => import('./pages/dex/AddLiquidity'));

// Lazy loading des pages d'administration
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminTransactions = lazy(() => import("./pages/admin/AdminTransactions"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminLogs = lazy(() => import("./pages/admin/AdminLogs"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminRates = lazy(() => import("./pages/admin/AdminRates"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminAlerts = lazy(() => import("./pages/admin/AdminAlerts"));
const AdminSecurity = lazy(() => import("./pages/admin/AdminSecurity"));
const AdminBbtDashboard = lazy(() => import('./pages/admin/AdminBbtDashboard'));
const AdminAirdrop = lazy(() => import('./pages/admin/AdminAirdrop'));
const AdminMassEmail = lazy(() => import('./pages/admin/AdminMassEmail'));

// Composant de chargement pour Suspense
const PageLoader = () => (
  <div className="flex justify-center items-center min-h-[50vh]">
    <LoadingSpinner text="Chargement de la page..." />
  </div>
);

// Composant PrivateRoute pour protéger les routes utilisateur
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <PageLoader />;

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PageLoader />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  if (!isAdmin) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Gestionnaire global pour les événements d'expiration de session
const AppSessionHandler = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const handleSessionExpired = (event) => {
      console.log("[App] Session expirée détectée, navigation vers login");
      
      // Seulement rediriger si on n'est pas déjà sur une page d'auth
      if (!location.pathname.startsWith('/login') && 
          !location.pathname.startsWith('/register') &&
          !location.pathname.startsWith('/verify-email') &&
          !location.pathname.startsWith('/forgot-password') &&
          !location.pathname.startsWith('/reset-password')) {
        navigate('/login?session=expired', { replace: true });
      }
    };

    window.addEventListener('authSessionExpired', handleSessionExpired);
    
    return () => {
      window.removeEventListener('authSessionExpired', handleSessionExpired);
    };
  }, [navigate, location.pathname]);

  return children;
};

const AppContent = () => {
  const { darkMode } = useTheme(); // Maintenant, c'est correct
  const location = useLocation();
  const isTelegramMiniApp = location.pathname.startsWith('/telegram/');

  return (
    <>
      <WebSocketMiddleware>
        <AppSessionHandler>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Routes d'authentification */}
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />
                <Route path="/resend-verification" element={<ResendVerification />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
              </Route>

              {/* Routes publiques avec layout */}
              <Route path="/" element={<PublicLayout />}>
                <Route index element={<Home />} />
                <Route path="home" element={<Home />} />
                <Route path="bbt" element={<BBTPage />} />
                <Route path="bbt-landing" element={<BBTPage />} />
                <Route path="airdrop" element={<AirdropPage />} />
                <Route path="tokenomics" element={<Tokenomics />} />
                <Route path="faq" element={<FAQ />} />
                <Route path="terms" element={<Terms />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="cookies" element={<CookiesPolicy />} />
                <Route path="aml-kyc" element={<AmlKycPolicy />} />
                <Route path="about" element={<About />} />
                <Route path="support" element={<Support />} />
                <Route path="blog" element={<Blog />} />
                <Route path="fees" element={<FeesPage />} />
              </Route>

              {/* Routes admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="transactions" element={<AdminTransactions />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="logs" element={<AdminLogs />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="rates" element={<AdminRates />} />
                <Route path="alerts" element={<AdminAlerts />} />
                <Route path="security" element={<AdminSecurity />} />
                <Route path="bbt" element={<AdminBbtDashboard />} />
                <Route path="airdrops" element={<AdminAirdrop />} />
                <Route path="mass-email" element={<AdminMassEmail />} />
              </Route>

              {/* Routes utilisateur protégées */}
              <Route
                element={
                  <PrivateRoute>
                    <MainLayout />
                  </PrivateRoute>
                }
              >
                <Route path="/dashboard" element={<Home />} />
                <Route path="/wallet" element={<Wallet />} />
                <Route path="/exchange" element={<Exchange />} />
                <Route path="/transfer-money" element={<TransferMoney />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/transactions/:id" element={<TransactionDetail />} />
                <Route path="/affiliation" element={<Affiliation />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/confirm-transaction" element={<ConfirmTransaction />} />
                <Route path="/transaction-processing" element={<TransactionProcessing />} />
                <Route path="/money-ops" element={<MoneyOps />} />
                <Route path="/p2p-transfer" element={<P2PTransferPage />} />
                <Route path="/wallet/deposit/rub" element={<RussianBankDeposit />} />
                <Route path="/dex/swap" element={<SwapPage />} />
                <Route path="/dex" element={<Navigate to="/dex/swap" replace />} />
                <Route path="/dex/liquidity" element={<Navigate to="/dex/liquidity/add" replace />} />
                <Route path="/dex/liquidity/add" element={<AddLiquidity />} />
              </Route>

              {/* Routes d'authentification supplémentaires */}
              <Route path="/email-verification-sent" element={<EmailVerificationSent />} />

              {/* Telegram Mini App — sans layout (Option C) */}
              <Route
                path="/telegram/transfer"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <TelegramTransfer />
                  </Suspense>
                }
              />

              {/* Fallback - redirection vers l'accueil */}
              <Route path="*" element={<Error404 />} />
            </Routes>
          </Suspense>
        </AppSessionHandler>
      </WebSocketMiddleware>
      
      {/* ChatBot — masqué dans la Mini App Telegram */}
      {!isTelegramMiniApp && <ChatBot />}
      
      {/* Bannière BBT flottante — masquée dans la Mini App Telegram */}
      {!isTelegramMiniApp && <BBTLaunchBanner variant="floating" />}
      
      {/* ToastContainer est rendu dans main.jsx — pas de doublon ici */}

      {!isTelegramMiniApp && <LaunchPromoPopup />}
    </>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
